import { CriterionEditorFields } from '@/components/admin/criteres/criterion-editor-fields';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { criteriaApi } from '@/lib/api';
import type { CriterionDraft } from '@/lib/criterion-draft';
import { CRITERION_DRAFT_KIT } from '@/lib/criterion-draft';
import { CRITERION_WORDING } from '@/lib/criterion-wording';
import { duplicateExample } from '@/lib/duplication';
import type {
  CriterionContent,
  CriterionDetail,
  CriterionDraftContent,
  CriterionVersion,
  VersionSummary,
} from '@matter_extractor/shared';
import { useNavigate } from '@tanstack/react-router';

/**
 * L'éditeur d'un critère : le patron de §4, plus le formulaire de §6.
 *
 * Everything this screen does that a persona's editor will do identically - the
 * head, the history, « utilisé dans », the retirement, the three boxes, the
 * debounced autosave and its optimistic control, the rename on blur - is
 * `VersionedEditor`. What is left here is the four things only a criterion
 * knows: its API, its draft kit, its wording, and its form.
 *
 * ## The form is bound here and not inside the layer
 *
 * `CriterionEditorFields` takes one callback per field, which is what makes it
 * mountable on its own - §13's guided form will mount the persona's the same
 * way. The layer, blind to content, can only offer « here is the draft, hand me
 * the next one », so the mapping between the two lives in this file: five lines
 * that name the criterion's fields, and nothing else.
 *
 * ## The two cache keys
 *
 * `['criterion', email, id]` holds a `CriterionDetail`; `['criteria', email]`
 * holds the card list. Different prefixes on purpose: the trap of issue #16 is
 * two readers agreeing on a key and disagreeing on what is under it, and
 * `useQuery` is generic per component so nothing would catch the drift.
 */

export interface CriterionEditorProps {
  /** The entity, or null for a criterion that does not exist yet. */
  detail: CriterionDetail | null;
  /** A version opened read-only by « Voir », or null. */
  viewing: CriterionVersion | null;
  onCloseView: () => void;
  onView: (version: VersionSummary) => void;
  /** Re-read the entity from the server - what the stale-draft box offers. */
  onReload: () => void;
  /**
   * Select the name field on opening (§4.2): true on the editor a duplication
   * has just navigated to, which the route reads off `?copie`.
   */
  selectName?: boolean;
}

export function CriterionEditor({
  detail,
  viewing,
  onCloseView,
  onView,
  onReload,
  selectName = false,
}: CriterionEditorProps) {
  const navigate = useNavigate();

  return (
    <VersionedEditor<CriterionContent, CriterionDraftContent, CriterionDraft>
      wording={CRITERION_WORDING}
      api={criteriaApi}
      kit={CRITERION_DRAFT_KIT}
      keys={{ detail: 'criterion', list: 'criteria' }}
      hasReferents
      detail={detail}
      viewing={viewing}
      onCloseView={onCloseView}
      onView={onView}
      onReload={onReload}
      onCreated={(criterionId) =>
        void navigate({ to: '/admin/criteres/$criterionId', params: { criterionId } })
      }
      selectName={selectName}
      renderFields={({ draft, readOnly, nameError, onDraft, onNameCommit }) => (
        <CriterionEditorFields
          draft={draft}
          readOnly={readOnly}
          nameError={nameError}
          onName={(name) => onDraft({ ...draft, name })}
          onNameCommit={onNameCommit}
          onDirection={(direction) => onDraft({ ...draft, direction })}
          onExample={(index, next) =>
            onDraft({
              ...draft,
              examples: draft.examples.map((example, at) => (at === index ? next : example)),
            })
          }
          onAddExample={() => onDraft({ ...draft, examples: [...draft.examples, ''] })}
          onRemoveExample={(index) =>
            onDraft({ ...draft, examples: draft.examples.filter((_, at) => at !== index) })
          }
          // The copy lands just after its source, and the cap of thirty is
          // held by `duplicateExample`, which answers `null` rather than
          // overflowing it (§4.2, §6).
          onDuplicateExample={(index) => {
            const examples = duplicateExample(draft.examples, index);
            if (examples !== null) onDraft({ ...draft, examples });
          }}
          // One line, like the five above it: the form sends back a complete
          // scoring - or null - and the edits that built it are the pure
          // functions of `criterion-draft.ts`.
          onScoring={(scoring) => onDraft({ ...draft, scoring })}
        />
      )}
    />
  );
}
